import { useState, useCallback } from 'react';

/**
 * Hook for managing language selection functionality
 * @returns {Object} Language state and handlers
 */
const useLanguages = () => {
  // All supported languages organized by region
  const allLanguages = {
    "European": {
      "English": "en",
      "Russian": "ru",
      "Spanish": "es",
      "French": "fr",
      "German": "de",
      "Italian": "it",
      "Portuguese": "pt",
      "Dutch": "nl",
      "Polish": "pl",
      "Ukrainian": "uk",
      "Romanian": "ro",
      "Czech": "cs",
      "Slovak": "sk",
      "Hungarian": "hu",
      "Greek": "el",
      "Swedish": "sv",
      "Norwegian": "no",
      "Danish": "da",
      "Finnish": "fi",
      "Icelandic": "is",
      "Albanian": "sq",
      "Serbian": "sr",
      "Croatian": "hr",
      "Bosnian": "bs",
      "Slovenian": "sl",
      "Macedonian": "mk",
      "Belarusian": "be",
      "Bulgarian": "bg",
      "Lithuanian": "lt",
      "Latvian": "lv",
      "Estonian": "et",
      "Irish": "ga",
      "Welsh": "cy",
      "Maltese": "mt",
      "Basque": "eu",
      "Galician": "gl",
      "Catalan": "ca"
    },
    "Asian": {
      "Chinese (Simplified)": "zh",
      "Chinese (Traditional)": "zh-TW",
      "Japanese": "ja",
      "Korean": "ko",
      "Hindi": "hi",
      "Bengali": "bn",
      "Urdu": "ur",
      "Punjabi": "pa",
      "Gujarati": "gu",
      "Tamil": "ta",
      "Telugu": "te",
      "Kannada": "kn",
      "Malayalam": "ml",
      "Marathi": "mr",
      "Assamese": "as",
      "Nepali": "ne",
      "Sinhala": "si",
      "Thai": "th",
      "Vietnamese": "vi",
      "Indonesian": "id",
      "Malay": "ms",
      "Filipino (Tagalog)": "tl",
      "Burmese": "my",
      "Mongolian": "mn",
      "Pashto": "ps",
      "Persian (Farsi)": "fa",
      "Kazakh": "kk",
      "Kyrgyz": "ky",
      "Uzbek": "uz",
      "Turkmen": "tk",
      "Tajik": "tg"
    },
    "Middle_Eastern_and_African": {
      "Arabic": "ar",
      "Hebrew": "he",
      "Amharic": "am",
      "Swahili": "sw",
      "Somali": "so",
      "Hausa": "ha",
      "Yoruba": "yo",
      "Igbo": "ig"
    },
    "Other": {
      "Awadhi": "awa",
      "Bhojpuri": "bho",
      "Maithili": "mai",
      "Sindhi": "sd",
      "Konkani": "kok",
      "Rajasthani": "raj",
      "Chhattisgarhi": "hne",
      "Dogri": "doi",
      "Santali": "sat"
    }
  };

  // Convert the nested object structure to a flat array for use in the UI
  const languages = Object.entries(allLanguages).flatMap(([region, langs]) => {
    return Object.entries(langs).map(([name, code]) => ({
      region,
      name,
      code
    }));
  });

  // Language dropdown state
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en'); // Store just the code

  // Handle right-click to show language dropdown
  const handleRightClick = useCallback((e) => {
    e.preventDefault();
    setShowLanguageDropdown(true);
  }, []);

  // Handle language selection
  const handleLanguageSelect = useCallback((langCode) => {
    setSelectedLanguage(langCode);
    setShowLanguageDropdown(false);
  }, []);

  // Get language name by code
  const getLanguageName = useCallback((code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : 'English';
  }, [languages]);

  return {
    languages,
    allLanguages,
    selectedLanguage,
    showLanguageDropdown,
    setShowLanguageDropdown,
    handleLanguageSelect,
    handleRightClick,
    getLanguageName
  };
};

export default useLanguages; 